// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
import { isEntityAction } from "@ms/planner-actions/lib/local/utils/ActionUtils";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
// Constants
import { ClientType } from "@ms/uno-constants/lib/local/AppConstants";
import { StoreEntityState, TableName } from "@ms/uno-constants/lib/local/StoreConstants";
import { ProjectSubscriptionState } from "@ms/uno-constants/lib/local/UpsellConstants";
import { FormerMember } from "@ms/planner-models/lib/local/client/user/FormerUser";
import { UnknownUser } from "@ms/planner-models/lib/local/client/user/UnknownUser";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { EntitySlice } from "../../slice/EntitySlice";
import { Slice } from "../../slice/Slice";
import { filterPersistedTablesForStore } from "../../utilities/StoreUtilities";
import { Store } from "../Store";
import { calculateRemainingDaysInTrial } from "@ms/uno-utilities/lib/local/UpsellUtilities";
import compact from "lodash/compact";
import { Temporal } from "temporal-polyfill-lite";
import { AbsentStoreEntityStatus } from "../../utilities/StatusUtilities";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
/**
 * Store for user related data using Slice and UpdatableSlice patterns
 */ export class UserStore extends EventPublishingStore {
    get name() {
        return Store.User;
    }
    getPersistedTables() {
        return filterPersistedTablesForStore(Store.User, {
            [TableName.UserPhotos]: this.userPhotosSlice.table(),
            ...this.userPhotosSlice.statusTables()
        });
    }
    getUserById(id) {
        return this.usersSlice.get(id);
    }
    getUserSettings(readBaseline = false) {
        return this.userSettingSlice.get(this.defaultKey, readBaseline);
    }
    getUserPhoto(userId) {
        const result = this.userPhotosSlice.get(userId);
        const user = this.getUserById(userId).data;
        if (result.data == null && (user == null || !user.displayName)) {
            // We don't have either user or user displayName for placeholder, fallback to FormerMember.Photo.
            // By default, preserve the underlying fetch state (in-flight or failures like
            // NotFound/Inaccessible/UnableToFetch) so callers don't keep refetching after a 4xx.
            // When the changegate is enabled, revert to the legacy behavior of always reporting Present.
            const newStatus = this.configProvider().changeGates.DisablePreserveUserPhotoFetchStatus ? {
                state: StoreEntityState.Present,
                isFetching: false,
                timestamp: null
            } : result.status;
            return {
                data: FormerMember.Photo,
                status: newStatus
            };
        }
        return result;
    }
    getUsersByKeyword(keyword) {
        const usersByKeywordResult = this.userSearchSlice.get(keyword);
        if (usersByKeywordResult?.data == null) {
            return {
                data: null,
                status: usersByKeywordResult.status
            };
        }
        // Check if the results have expired
        if (this.isUsersByKeywordResultExpired(usersByKeywordResult.status.timestamp)) {
            return {
                data: null,
                status: AbsentStoreEntityStatus
            };
        }
        const userIds = usersByKeywordResult.data;
        const users = compact(userIds.map((userId)=>{
            const userResult = this.getUserById(userId);
            if (userResult.data) {
                return userResult.data;
            }
        }));
        return {
            data: users,
            status: usersByKeywordResult.status
        };
    }
    getUserByEmail(email) {
        if (email === FormerMember.Mail) {
            return {
                data: UnknownUser.getFormerMember(),
                status: AbsentStoreEntityStatus
            };
        }
        // Try to find user by email index
        const usersByEmail = this.usersSlice.getCollection({
            name: "email",
            value: email
        });
        if (usersByEmail.data.length > 0) {
            const userStatus = usersByEmail.data.map((user)=>{
                return this.getUserById(user.id).status;
            });
            return {
                data: usersByEmail.data[0],
                status: userStatus[0]
            };
        }
        // Try to find user by principal name index
        const usersByPrincipal = this.usersSlice.getCollection({
            name: "principalName",
            value: email
        });
        if (usersByPrincipal.data.length > 0) {
            const userStatus = usersByPrincipal.data.map((user)=>{
                return this.getUserById(user.id).status;
            });
            return {
                data: usersByPrincipal.data[0],
                status: userStatus[0]
            };
        }
        return {
            data: null,
            status: AbsentStoreEntityStatus
        };
    }
    getUserRoamingData() {
        return this.roamingDataSlice.get(this.defaultKey);
    }
    shouldUserSeeTrialReminder(highestTrialSubscription) {
        // Check user's subscription status
        const remainingDaysInTrial = calculateRemainingDaysInTrial(highestTrialSubscription.expiryDate);
        const eligibleStatus = highestTrialSubscription.subscriptionStatus === ProjectSubscriptionState.Active && remainingDaysInTrial <= 15 && remainingDaysInTrial > 0 || highestTrialSubscription.subscriptionStatus === ProjectSubscriptionState.Disabled;
        // Check user's roaming data
        const userRoamingData = this.getUserRoamingData();
        if (!userRoamingData?.data) {
            return false;
        }
        return userRoamingData.data.showTrialReminder && eligibleStatus;
    }
    shouldUserSeeFirstRunExperience() {
        // Check user's roaming data
        const userRoamingData = this.getUserRoamingData();
        if (userRoamingData.data == null) {
            return false;
        }
        const clientType = this.configProvider().sessionMetaData.appMetadata.clientType;
        let freKey;
        if (clientType === ClientType.Teams) {
            freKey = "lastFirstRunExperienceDate";
        } else if (clientType === ClientType.Web) {
            freKey = "lastFirstRunExperienceDateWeb";
        } else {
            return false;
        }
        if (userRoamingData.data[freKey] == null) {
            return true;
        }
        // Convert to Temporal for date comparison
        const showFirstRunExperienceAfter = Temporal.Instant.fromEpochMilliseconds(this.configProvider().settings.showFirstRunExperienceAfter.getTime());
        const lastFirstRunExperienceDate = userRoamingData.data[freKey]; // We already checked for null above
        return Temporal.Instant.compare(showFirstRunExperienceAfter, lastFirstRunExperienceDate) > 0;
    }
    handleAction(action) {
        if (!this.shouldHandleAction(action)) {
            return;
        }
        let storeChanged = false;
        switch(action.type){
            case ActionType.FetchEntityAction:
                storeChanged = this.usersSlice.handleFetchStarted(action.entityId);
                break;
            case ActionType.FetchEntitySucceededAction:
                storeChanged = this.usersSlice.handleFetchSucceeded(action.entity.id, action.entity);
                break;
            case ActionType.FetchEntityFailedAction:
                storeChanged = this.usersSlice.handleFetchFailed(action.entityId, action.response);
                break;
            case ActionType.FetchUserPhotoSucceededAction:
                storeChanged = this.userPhotosSlice.handleFetchSucceeded(action.userId, action.userPhoto, undefined, action.status);
                break;
            case ActionType.FetchUserPhotoFailedAction:
                if (action.userId) {
                    storeChanged = this.userPhotosSlice.handleFetchFailed(action.userId, action.response, action.status);
                }
                break;
            case ActionType.FetchUsersByKeywordAction:
                storeChanged = this.userSearchSlice.handleFetchStarted(action.keyword);
                break;
            case ActionType.FetchUsersByKeywordSucceededAction:
                storeChanged = this.handleFetchUsersByKeywordSucceededAction(action);
                break;
            case ActionType.FetchUsersByKeywordFailedAction:
                storeChanged = this.userSearchSlice.handleFetchFailed(action.keyword, action.response);
                break;
            case ActionType.FetchModernGroupMembersSucceededAction:
                storeChanged = this.handleFetchUsersSucceededAction(action.users, false);
                break;
            case ActionType.FetchModernGroupOwnersSucceededAction:
                storeChanged = this.handleFetchUsersSucceededAction(action.owners, false);
                break;
            case ActionType.FetchEntityCollectionSucceededAction:
                if (action.entityType === EntityType.User) {
                    storeChanged = this.handleFetchUsersSucceededAction(action.entities, true);
                }
                break;
            case ActionType.FetchRoamingUserDataSucceededAction:
                storeChanged = this.roamingDataSlice.handleFetchSucceeded(this.defaultKey, action.roamingUserData);
                break;
            case ActionType.UpdateRoamingUserDataSucceededAction:
                storeChanged = this.roamingDataSlice.handleFetchSucceeded(this.defaultKey, action.roamingUserData);
                break;
            case ActionType.FetchUserSettingsSucceededAction:
                storeChanged = this.handleFetchUserSettingsSucceededAction(action);
                break;
            case ActionType.UpdateUserSettingsAction:
                storeChanged = this.handleUpdateUserSettingsAction(action);
                break;
            case ActionType.UpdateUserSettingsSucceededAction:
                storeChanged = this.handleUpdateUserSettingsSucceededAction(action);
                break;
            case ActionType.UpdateUserSettingsFailedAction:
                storeChanged = this.handleUpdateUserSettingsFailedAction(action);
                break;
            case ActionType.UpdateRoamingUserDataFailedAction:
                storeChanged = this.roamingDataSlice.handleFetchFailed(this.defaultKey, action.response);
                break;
            case ActionType.FetchRoamingUserDataFailedAction:
                storeChanged = this.roamingDataSlice.handleFetchFailed(this.defaultKey, action.response);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                break;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    shouldHandleAction(action) {
        if (isEntityAction(action)) {
            const validEntityTypes = [
                EntityType.User
            ];
            return validEntityTypes.includes(action.entityType);
        }
        return true; // Handle all other actions
    }
    /**
     * Handle fetch users
     * @param users Array of users
     * @param shouldLogEmptyDisplayName Flag indicating whether to log empty display names
     */ handleFetchUsersSucceededAction(users, shouldLogEmptyDisplayName) {
        let storeChanged = false;
        users.forEach((user)=>{
            if (shouldLogEmptyDisplayName && user.displayName === "") {
                this.loggers.traceLogger.logTrace(0x1e185887 /* tag_4gf8h */ , TraceLevel.Warning, `User.displayName is empty [UserId=${user.id}]`);
            }
            storeChanged = this.usersSlice.handleFetchSucceeded(user.id, user) || storeChanged;
        });
        return storeChanged;
    }
    /**
     * Handler for FetchUserSettingsSucceededAction
     * @param action The fetch user settings succeeded action
     */ handleFetchUserSettingsSucceededAction(action) {
        const { userSettings } = action;
        return this.userSettingSlice.handleFetchSucceeded(this.defaultKey, userSettings);
    }
    /**
     * Handler for UpdateUserSettingsAction
     * @param action The update user settings action
     */ handleUpdateUserSettingsAction(action) {
        const { delta, updateKey } = action;
        // Get current user settings from slice
        const currentSettings = this.userSettingSlice.get(this.defaultKey);
        if (currentSettings.data == null) {
            const msg = `StoreException: UserSettings not found in store`;
            this.loggers.traceLogger?.logTrace(0x1e197423 /* tag_4gxq9 */ , TraceLevel.Error, msg);
            throw new Error(msg);
        }
        // Apply delta using UpdatableSlice methods
        const storeChanged = this.userSettingSlice.handleUpdateStarted(this.defaultKey, updateKey, delta);
        return storeChanged;
    }
    /**
     * Handler for UpdateUserSettingsSucceededAction
     * @param action The update user settings succeeded action
     */ handleUpdateUserSettingsSucceededAction(action) {
        const { userSettings, updateKey } = action;
        // Commit the pending update using UpdatableSlice
        const storeChanged = this.userSettingSlice.handleUpdateSucceeded(this.defaultKey, updateKey, userSettings);
        return storeChanged;
    }
    /**
     * Handler for UpdateUserSettingsFailedAction
     * @param action The update user settings failed action
     */ handleUpdateUserSettingsFailedAction(action) {
        const { response, updateKey } = action;
        // Get current user settings from slice
        const currentSettings = this.userSettingSlice.get(this.defaultKey);
        if (currentSettings.data == null) {
            const msg = `StoreException: UserSettings not found in store`;
            this.loggers.traceLogger?.logTrace(0x1e197422 /* tag_4gxq8 */ , TraceLevel.Error, msg);
            throw new Error(msg);
        }
        // Revert the delta using UpdatableSlice
        const storeChanged = this.userSettingSlice.handleUpdateFailed(this.defaultKey, updateKey, response);
        return storeChanged;
    }
    isUsersByKeywordResultExpired(fetchTimestamp) {
        if (fetchTimestamp == null) {
            return true; // If no timestamp, treat as expired
        }
        // If the data is older than expiry time, treat it as expired
        const expiryTime = this.configProvider().settings.userSearchResultsExpiryTimeInSeconds;
        const expiryInstant = Temporal.Now.instant().subtract({
            seconds: expiryTime
        });
        // Compare Temporal Instants directly
        return Temporal.Instant.compare(fetchTimestamp, expiryInstant) < 0;
    }
    /**
     * Handler for FetchUsersByKeywordSucceededAction
     */ handleFetchUsersByKeywordSucceededAction(action) {
        let storeChanged = false;
        const { keyword, users } = action;
        // Get existing search results to check if we need to update
        const existingResults = this.userSearchSlice.get(keyword);
        // Only update empty or expired results
        if (existingResults.data == null || this.isUsersByKeywordResultExpired(existingResults.status.timestamp)) {
            // Store individual users first
            users.forEach((user)=>{
                storeChanged = this.usersSlice.handleFetchSucceeded(user.id, user) || storeChanged;
            });
            // Create new result
            const newResult = users.map((user)=>user.id);
            // Update the search result using slice
            storeChanged = this.userSearchSlice.handleFetchSucceeded(keyword, newResult) || storeChanged;
            return storeChanged;
        }
        // If we have valid, non-expired results, no need to update
        return storeChanged;
    }
    constructor(user, loggers, configProvider){
        super(), this.loggers = loggers, this.configProvider = configProvider, /** Stored User Data */ this.usersSlice = new Slice(TableName.Users, {
            email: (user)=>user.mail,
            principalName: (user)=>user.userPrincipalName
        }, this.configProvider), this.userPhotosSlice = new Slice(TableName.UserPhotos, {}, this.configProvider), this.userSearchSlice = new Slice(TableName.UserSearch, {}, this.configProvider), this.userSettingSlice = new EntitySlice(TableName.UserSettings, {}, this.configProvider), this.roamingDataSlice = new Slice(TableName.RoamingData, {}, this.configProvider), // Default key
        this.defaultKey = "defaultKey";
        // Initialize with the current user using slice
        this.usersSlice.handleFetchSucceeded(user.id, user);
    }
}
